import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import emailService from '../services/emailService';
import SentimentChart from '../components/charts/SentimentChart';
import CommunicationChart from '../components/charts/CommunicationChart';
import CorrespondentChart from '../components/charts/CorrespondentChart';
import EmailAnalysisModal from '../components/EmailAnalysisModal';

function Insights() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aggregatedData, setAggregatedData] = useState(null);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Ne faire l'appel API que si l'utilisateur est authentifié
    if (isAuthenticated) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger les statistiques de base
      const statsData = await emailService.getStats();
      setStats(statsData);

      // Charger les données agrégées pour différents types d'analyses
      const types = ['communication_patterns', 'sentiment_trends', 'correspondent_analysis', 'topic_evolution'];
      const aggregatedResults = {};

      for (const type of types) {
        try {
          const data = await emailService.getAggregatedData(type);
          aggregatedResults[type] = data;
        } catch (err) {
          console.warn(`Erreur lors du chargement des données ${type}:`, err);
          // Données de fallback
          aggregatedResults[type] = getFallbackData(type);
        }
      }

      setAggregatedData(aggregatedResults);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getFallbackData = (type) => {
    switch (type) {
      case 'communication_patterns':
        return {
          total_emails: stats?.totalEmails || 0,
          time_period: { start: null, end: null },
          top_senders: [],
          frequency_analysis: { daily_average: 0, peak_days: [] },
          thread_analysis: []
        };
      case 'sentiment_trends':
        return {
          sentiment_data: [],
          total_sentiment_analyses: 0,
          average_sentiment: stats?.averageSentiment || 0,
          sentiment_trend: 'stable'
        };
      case 'correspondent_analysis':
        return {
          top_correspondents: []
        };
      case 'topic_evolution':
        return {
          topics_by_period: []
        };
      default:
        return {};
    }
  };

  const getPercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  const handleChartClick = (emailId) => {
    if (emailId) {
      setSelectedEmailId(emailId);
      setShowAnalysisModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowAnalysisModal(false);
    setSelectedEmailId(null);
  };

  if (loading) {
    return (
      <div className="container-fluid py-6">
        <div className="text-center py-12">
          <div className="spinner-border text-blue-600 mb-4" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="text-slate-600">Chargement des analyses avancées...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-6">
        <div className="alert alert-danger mb-6" role="alert">
          <i className="bi bi-exclamation-triangle mr-2"></i>
          {error}
        </div>
        <button className="btn btn-primary" onClick={loadData}>
          <i className="bi bi-arrow-clockwise mr-2"></i>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid py-6">
      {/* Modal d'analyse d'email */}
      {showAnalysisModal && (
        <EmailAnalysisModal
          emailId={selectedEmailId}
          onClose={handleCloseModal}
        />
      )}
      {/* Header */}
      <div className="page-header">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="page-title">
              <i className="bi bi-lightbulb mr-3"></i>
              Analyses Avancées
            </h1>
            <p className="page-subtitle">
              Insights approfondis et tendances de vos données
            </p>
          </div>
          <button className="btn btn-outline-primary" onClick={loadData}>
            <i className="bi bi-arrow-clockwise mr-2"></i>
            Actualiser
          </button>
        </div>
      </div>

      {stats && aggregatedData ? (
        <>
          {/* Advanced Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Communication Patterns */}
            <div className="analysis-card">
              <div className="card-header">
                <h5 className="text-lg font-semibold mb-0">
                  <i className="bi bi-graph-up mr-2"></i>
                  Patterns de Communication Avancés
                </h5>
              </div>
              <div className="card-body">
                <CommunicationChart
                  data={aggregatedData.communication_patterns}
                  onChartClick={handleChartClick}
                />
              </div>
            </div>

            {/* Sentiment Trends */}
            <div className="analysis-card">
              <div className="card-header">
                <h5 className="text-lg font-semibold mb-0">
                  <i className="bi bi-heart mr-2"></i>
                  Analyse de Sentiment Avancée
                </h5>
              </div>
              <div className="card-body">
                <SentimentChart
                  data={aggregatedData.sentiment_trends}
                  onChartClick={handleChartClick}
                />
              </div>
            </div>
          </div>

          {/* Correspondent Analysis */}
          <div className="analysis-card mb-8">
            <div className="card-header">
              <h5 className="text-lg font-semibold mb-0">
                <i className="bi bi-people mr-2"></i>
                Analyse des Correspondants
              </h5>
            </div>
            <div className="card-body">
              <CorrespondentChart
                data={aggregatedData.correspondent_analysis}
                totalEmails={stats.totalEmails || 0}
              />
            </div>
          </div>

          {/* Topic Evolution - Version améliorée avec timeline */}
          <div className="analysis-card mb-8">
            <div className="card-header">
              <h5 className="text-lg font-semibold mb-0">
                <i className="bi bi-tags mr-2"></i>
                Évolution des Sujets
              </h5>
            </div>
            <div className="card-body">
              {aggregatedData.topic_evolution?.topics_by_period?.length > 0 ? (
                <div className="space-y-4">
                  {/* Timeline graphique */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <h6 className="font-semibold mb-3 text-slate-700">Timeline des sujets</h6>
                    <div className="h-32 relative">
                      <svg width="100%" height="100%" className="min-h-[128px]">
                        {aggregatedData.topic_evolution.topics_by_period.slice(0, 15).map((topic, index) => {
                          const x = (index / Math.min(aggregatedData.topic_evolution.topics_by_period.length - 1, 14)) * 100;
                          const y = 100 - (Math.min(topic.count / 10, 1) * 100); // Normaliser la hauteur
                          
                          return (
                            <g key={index}>
                              <circle
                                cx={`${x}%`}
                                cy={`${y}%`}
                                r="4"
                                fill="#8b5cf6"
                              />
                              {index > 0 && (
                                <line
                                  x1={`${(index - 1) / Math.min(aggregatedData.topic_evolution.topics_by_period.length - 1, 14) * 100}%`}
                                  y1={`${100 - (Math.min(aggregatedData.topic_evolution.topics_by_period[index - 1].count / 10, 1) * 100)}%`}
                                  x2={`${x}%`}
                                  y2={`${y}%`}
                                  stroke="#8b5cf6"
                                  strokeWidth="2"
                                />
                              )}
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  </div>

                  {/* Liste des sujets */}
                  <div className="space-y-3">
                    {aggregatedData.topic_evolution.topics_by_period.slice(0, 10).map((topic, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-slate-100">
                        <div className="flex-1">
                          <div className="font-medium text-slate-800 truncate">
                            {topic.subject || 'Sujet non identifié'}
                          </div>
                          <div className="text-sm text-slate-500">
                            {topic.date ? new Date(topic.date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="badge bg-blue-100 text-blue-800">{topic.count}</span>
                          <span className="text-sm text-slate-500">
                            {getPercentage(topic.count, stats.analyzedEmails)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center text-slate-500 py-8">
                  <i className="bi bi-chat-quote text-4xl mb-3 block"></i>
                  <p>Aucune évolution de sujet détectée</p>
                </div>
              )}
            </div>
          </div>

          {/* Recommendations */}
          <div className="analysis-card">
            <div className="card-header">
              <h5 className="text-lg font-semibold mb-0">
                <i className="bi bi-lightbulb mr-2"></i>
                Recommandations
              </h5>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-start">
                      <i className="bi bi-graph-up text-blue-600 mr-3 mt-1"></i>
                      <div>
                        <h6 className="font-semibold text-blue-900 mb-1">Optimiser l'analyse</h6>
                        <p className="text-sm text-blue-700">
                          {getPercentage(stats.analyzedEmails, stats.totalEmails) < 50
                            ? "Augmentez votre taux d'analyse en traitant plus d'emails avec l'IA."
                            : "Excellent taux d'analyse ! Continuez à exploiter pleinement vos données."
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-start">
                      <i className="bi bi-heart text-green-600 mr-3 mt-1"></i>
                      <div>
                        <h6 className="font-semibold text-green-900 mb-1">Sentiment</h6>
                        <p className="text-sm text-green-700">
                          {stats.averageSentiment > 0.5
                            ? "Votre correspondance générale est positive. Excellent !"
                            : stats.averageSentiment > 0
                            ? "Sentiment globalement neutre à positif."
                            : "Envisagez d'analyser les facteurs influençant le sentiment."
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-start">
                      <i className="bi bi-cloud text-purple-600 mr-3 mt-1"></i>
                      <div>
                        <h6 className="font-semibold text-purple-900 mb-1">Stockage</h6>
                        <p className="text-sm text-purple-700">
                          {stats.driveAvailable
                            ? "Google Drive est configuré et opérationnel."
                            : "Configurez Google Drive pour un stockage optimal de vos pièces jointes."
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50 rounded-lg">
                    <div className="flex items-start">
                      <i className="bi bi-shield-check text-amber-600 mr-3 mt-1"></i>
                      <div>
                        <h6 className="font-semibold text-amber-900 mb-1">Sécurité</h6>
                        <p className="text-sm text-amber-700">
                          Vos données sont sécurisées et chiffrées. Continuez à utiliser Discover en toute confiance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <i className="bi bi-graph-down text-6xl text-slate-300 mb-4 block"></i>
          <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucune donnée disponible</h3>
          <p className="text-slate-500 mb-6">Importez des emails pour voir les analyses avancées</p>
          <button
            onClick={() => window.location.href = '/'}
            className="btn btn-primary"
          >
            <i className="bi bi-upload mr-2"></i>
            Importer des emails
          </button>
        </div>
      )}
    </div>
  );
}

export default Insights;